import { getCurrentInstance, onMounted, ref, type Ref } from 'vue'

import DashboardService from '@/services/clubs/dashboardService'
import { handleRequest } from '@/utils/handleRequest';

type PermissionMap = Record<string, Record<string, boolean>>

interface CacheEntry {
  permissions: Ref<PermissionMap>
  loaded: boolean
  inFlight: Promise<any> | null
}

// Permissions are shared per club so the dashboard route guard's fetch is
// reused by every component on the page instead of each one refetching on
// mount. Keyed by clubId so switching clubs never serves stale permissions.
const cache = new Map<string, CacheEntry>()

function entryFor(clubId: string): CacheEntry {
  let entry = cache.get(clubId)
  if (!entry) {
    entry = { permissions: ref({}), loaded: false, inFlight: null }
    cache.set(clubId, entry)
  }
  return entry
}

export function useClubDashboardPolicy(clubId: string | number) {
  const dashboardService = DashboardService.create()

  const entry = entryFor(String(clubId))
  const permissions = entry.permissions

  // Route guards call fetchPermissions() and get a fresh result on every
  // navigation; components mounted behind a guard read the cached copy via
  // { force: false } so a page load costs a single request.
  const fetchPermissions = async ({ force = true } = {}) => {
    if (!force && entry.loaded) return { success: true }
    if (entry.inFlight) return await entry.inFlight

    entry.inFlight = handleRequest(
      () => dashboardService.getDashboardPermissions(clubId),
      {
        onSuccess: (response: any) => {
          permissions.value = response.data.permissions
          entry.loaded = true
        }
      }
    )

    try {
      return await entry.inFlight
    } finally {
      entry.inFlight = null
    }
  }

  const canViewClubEmails = () => permissions.value?.club_emails?.index ?? false
  const canSubmitClubEmailRequest = () => permissions.value?.club_emails?.create ?? false

  const canUpdateClubLogo = () => permissions.value?.club?.update_logo ?? false
  const canUpdateClubProfile = () => permissions.value?.club?.update_profile ?? false
  const canViewUpdateClubProfile = () => permissions.value?.club?.view_update_profile ?? false

  const canViewActiveTermAcknowledgementForms = () => permissions.value?.term_acknowledgement_forms?.view_active ?? false
  const canViewPendingTermAcknowledgementFormApprovals = () => permissions.value?.term_acknowledgement_forms?.view_pending_signing_authority ?? false
  const canViewTermAcknowledgementFormSubmissionAlert = () => permissions.value?.term_acknowledgement_forms?.submission_open ?? false

  const canViewEventForms = () => permissions.value?.event_forms?.view ?? false
  const canViewEventFormLink = () => permissions.value?.event_forms?.view_link ?? false
  const canViewPendingReimbursementRequests = () => permissions.value?.reimbursement_requests?.view_pending_signing_authority ?? false
  const canViewReimbursementRequests = () => permissions.value?.reimbursement_requests?.view ?? false
  const canViewOpenClubReimbursementRequests = () => permissions.value?.reimbursement_requests?.view_open ?? false
  const canViewCompletedReimbursementRequests = () => permissions.value?.reimbursement_requests?.view_completed ?? false

  const canViewMembers = () => permissions.value?.club_memberships?.view ?? false
  const canRemoveMember = () => permissions.value?.club_memberships?.remove ?? false
  const canViewSupport = () => permissions.value?.support?.view ?? false
  const canViewClubProfileRequests = () => permissions.value?.club_profile_requests?.view ?? false

  const canViewFinances = () => permissions.value?.finances?.view ?? false

  // Auto-fetch only when used inside a component; router guards call
  // fetchPermissions() explicitly and have no instance to mount against.
  if (getCurrentInstance()) {
    onMounted(async () => await fetchPermissions({ force: false }))
  }

  return {
    fetchPermissions,

    canViewClubEmails,
    canSubmitClubEmailRequest,

    canUpdateClubLogo,
    canUpdateClubProfile,
    canViewUpdateClubProfile,

    canViewActiveTermAcknowledgementForms,
    canViewPendingTermAcknowledgementFormApprovals,
    canViewTermAcknowledgementFormSubmissionAlert,

    canViewEventForms,
    canViewEventFormLink,
    canViewPendingReimbursementRequests,
    canViewReimbursementRequests,
    canViewOpenClubReimbursementRequests,
    canViewCompletedReimbursementRequests,

    canViewMembers,
    canRemoveMember,
    canViewSupport,
    canViewClubProfileRequests,

    canViewFinances
  }
}
